/**
 * wayai pull [--hub <id|name>] [-y] — fetch hub config from the platform and write local files
 *
 * The hub is derived from the workspace:
 *   - cwd is inside a hub folder → use that hub
 *   - exactly one hub in the hubs dir → use it
 *   - multiple hubs → require `--hub <uuid|folder-name>` to disambiguate
 *
 * For first-time pulls in an empty workspace, pass `--hub <uuid>` to fetch and
 * materialize the hub at `wayai-ws/hubs/<hub-slug>/` (or the legacy
 * `workspace/<hub-slug>/` for un-migrated repos — see `layout.ts`).
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import { requireAuth } from '../lib/auth.js';
import { ApiClient } from '../lib/api-client.js';
import { parseHubFolder } from '../lib/parser.js';
import { printDiff } from '../lib/diff-display.js';
import { confirm, UUID_RE } from '../lib/utils.js';
import { prepareResourcePayload } from '../lib/resource-files.js';
import { materializeHubFolder } from '../lib/hub-materializer.js';
import { printLocalFileChanges } from './push.js';
import {
  autoRenameHubFolder,
  filterPreviewHubs,
  findGitRoot,
  findEnclosingHubFolder,
  resolveHubFolder,
  resolveWorkspaceDir,
  scanWorkspaceHubs,
} from '../lib/workspace.js';
import { warnLayoutOnce, hubsDirLabel } from '../lib/layout.js';
import { ensureWorkspaceFiles } from '../lib/workspace-files.js';
import { requireRepoConfig } from '../lib/repo-config.js';
import { assertHubMatchesBinding, autoBindIfUnbound } from '../lib/hub-binding.js';
import type { HubAsCodePayload } from '../lib/types.js';

interface ParsedArgs {
  autoConfirm: boolean;
  hubSelector?: string;
}

function parseArgs(args: string[]): ParsedArgs {
  let autoConfirm = false;
  let hubSelector: string | undefined;

  for (let i = 0; i < args.length; i++) {
    const arg = args[i];
    if (arg === '--yes' || arg === '-y') {
      autoConfirm = true;
    } else if (arg === '--hub' && args[i + 1]) {
      hubSelector = args[++i];
    }
  }

  return { autoConfirm, hubSelector };
}

/** Resolve the target hub for the pull. May return a not-yet-materialized hub_id. */
export function resolveHubTarget(
  workspaceDir: string,
  selector: string | undefined,
): { hubId: string; hubFolder: string | null } {
  const wsLabel = hubsDirLabel(findGitRoot());
  const allHubs = scanWorkspaceHubs(workspaceDir);

  if (selector) {
    // Match against existing folders by hubId or basename. Searches ALL folders
    // (incl. production mirrors) so a mirror can be refreshed directly.
    const match = allHubs.find((h) =>
      h.hubId === selector || path.basename(h.hubFolder) === selector,
    );
    if (match) return { hubId: match.hubId, hubFolder: match.hubFolder };

    // Selector might be a UUID for a hub that hasn't been materialized yet.
    if (UUID_RE.test(selector)) return { hubId: selector, hubFolder: null };

    console.error(`No hub matching --hub ${selector} found in ${wsLabel}/. Pass a UUID for first-time pulls.`);
    process.exit(1);
  }

  const enclosing = findEnclosingHubFolder(workspaceDir);
  if (enclosing) return { hubId: enclosing.hubId, hubFolder: enclosing.hubFolder };

  // Auto-select ignores read-only production mirror folders so a preview + its
  // mirror still resolves to the single preview.
  const previewHubs = filterPreviewHubs(allHubs);

  if (previewHubs.length === 1) return { hubId: previewHubs[0].hubId, hubFolder: previewHubs[0].hubFolder };

  if (previewHubs.length === 0) {
    console.error(`No hub folders found in ${wsLabel}/. Pass --hub <uuid> to fetch a hub for the first time.`);
    process.exit(1);
  }

  console.error(`Multiple hubs found in ${wsLabel}/. Pass --hub <uuid|folder-name> to choose, or run from inside a hub folder:`);
  for (const h of previewHubs) {
    console.error(`  ${path.basename(h.hubFolder)} (${h.hubId})`);
  }
  process.exit(1);
}

export async function pullCommand(args: string[]): Promise<void> {
  const { autoConfirm, hubSelector } = parseArgs(args);
  const repoConfig = requireRepoConfig();
  const { organization_id: organizationId } = repoConfig;

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  // Determine the hubs directory for the active layout.
  const workspaceDir = resolveWorkspaceDir();
  const gitRoot = findGitRoot();
  if (gitRoot) warnLayoutOnce(gitRoot);
  // Seed the repo-root AGENTS.md + CLAUDE.md bootstrap if missing (absence-guarded).
  // Incidental notice to stderr — it's a side effect of pull, not its output.
  if (gitRoot) {
    for (const file of ensureWorkspaceFiles(gitRoot)) {
      console.error(`Created ${file}`);
    }
  }

  const { hubId, hubFolder: existingFolder } = resolveHubTarget(workspaceDir, hubSelector);

  // Fetch remote hub config
  console.log('Fetching hub configuration...');
  const payload = await client.pull(hubId, organizationId);

  if (payload.hub_environment === 'production') {
    // Directly-targeted production hub → materialize a read-only mirror (bare
    // slug, no preview suffix). No binding: the mirror is browse-only, and push
    // is blocked for production at the route + DO. Failure here is the primary
    // action's failure, so let it surface.
    const folder = await writeProductionMirror(workspaceDir, payload);
    console.log(`Production hub mirrored (read-only) → ${folder}`);
    return;
  }

  // Preview hub → enforce the worktree binding before writing editable source.
  assertHubMatchesBinding(hubId);

  let hubFolder = existingFolder;
  let wroteFiles = false;

  // If no existing folder, create path for first pull
  if (!hubFolder) {
    hubFolder = resolveHubFolder(
      workspaceDir,
      hubId,
      payload.hub.name,
      payload.hub_environment,
      payload.preview_label,
      payload.branch_name,
    );
    // Ensure parent directories exist
    fs.mkdirSync(path.dirname(hubFolder), { recursive: true });

    console.log('Writing hub configuration...');
    await materializeHubFolder(hubFolder, payload);
    const finalFolder = autoRenameHubFolder(hubFolder, payload.hub.name, payload.hub_environment, payload.hub_id, payload.preview_label, payload.branch_name);
    console.log(`Hub configuration written to ${finalFolder}`);
    wroteFiles = true;
  } else {
    // Hub folder exists — show diff before overwriting. The catch spans ONLY the
    // parse/diff phase: a materializer failure must propagate, because falling
    // into an overwrite retry would re-run the materializer on the same
    // (already-rewritten) payload and trip its double-run guard.
    let action: 'sync_metadata' | 'apply' | 'overwrite' = 'overwrite';
    try {
      const localPayload = parseHubFolder(hubFolder);
      const diffResult = await client.diff(hubId, prepareResourcePayload(localPayload, payload), 'pull', organizationId);

      // Connections are read-only references excluded from the diff engine.
      // Compare them separately so new/removed connections trigger a pull write.
      const connectionsChanged = !connectionsEqual(localPayload.connections, payload.connections);

      if (!diffResult.has_changes && !connectionsChanged) {
        // No semantic changes, but still write the remote payload to sync
        // metadata (tool IDs, connection IDs, etc.) that the diff engine ignores.
        action = 'sync_metadata';
      } else {
        console.log('\nChanges to apply locally:');
        if (connectionsChanged) {
          console.log('**Connections:** updated');
        }
        printDiff(diffResult.summary);

        if (!autoConfirm) {
          const ok = await confirm('Apply these changes?');
          if (!ok) {
            console.log('Cancelled.');
            return;
          }
        }
        action = 'apply';
      }
    } catch {
      // Local files can't be parsed — overwrite
      action = 'overwrite';
    }

    if (action === 'overwrite') console.log('Writing hub configuration...');
    const delta = await materializeHubFolder(hubFolder, payload);
    const finalFolder = autoRenameHubFolder(hubFolder, payload.hub.name, payload.hub_environment, payload.hub_id, payload.preview_label, payload.branch_name);
    wroteFiles = true;
    if (action === 'sync_metadata') {
      if (finalFolder === hubFolder) {
        // The diff engine saw no semantic change, but metadata the engine ignores (tool IDs,
        // connection IDs, etc.) may still have been written — NAME the files that actually
        // changed rather than a blanket "no config changes" line (gh #3410).
        printLocalFileChanges(delta);
      } else {
        console.log('Metadata synced; folder renamed.');
      }
    } else {
      // apply (user confirmed a diff) / overwrite (repaired an unparseable tree): name the
      // files that changed on disk too, not just the folder — any local write announces
      // itself (gh #3410). The first-pull branch above stays quiet: a brand-new folder is
      // all-new, so a full listing is noise (same reasoning as replicate's fresh case).
      console.log(`Hub configuration written to ${finalFolder}`);
      printLocalFileChanges(delta);
    }
  }

  // Auto-bind the worktree to this hub on first successful pull. No-op if a
  // binding already exists (even pointing at the same hub) or if not in git.
  if (wroteFiles) autoBindIfUnbound(hubId);

  // Mirror the linked production hub read-only so the agent always has the live
  // production config alongside the preview. Best-effort — the preview pull has
  // already succeeded, so a mirror failure must NOT fail the command.
  if (payload.production_hub_id) {
    await mirrorLinkedProduction(client, workspaceDir, payload.production_hub_id, organizationId);
  }
}

/**
 * Materialize a production hub as a READ-ONLY mirror folder (bare slug, no
 * preview suffix). The CLI cannot push production (blocked at the route + DO),
 * so this is a browse-only snapshot, refreshed on every pull of its preview.
 * Never binds the worktree. Returns the final folder path.
 */
export async function writeProductionMirror(
  workspaceDir: string,
  prodPayload: HubAsCodePayload,
): Promise<string> {
  const folder = resolveHubFolder(workspaceDir, prodPayload.hub_id, prodPayload.hub.name, 'production', null, null);
  fs.mkdirSync(path.dirname(folder), { recursive: true });
  // Read-only mirror: no agent-context authoring scaffold (browse-only, never synced).
  await materializeHubFolder(folder, prodPayload, { seedAgentContext: false });
  const finalFolder = autoRenameHubFolder(folder, prodPayload.hub.name, 'production', prodPayload.hub_id, null, null);
  prependMirrorMarker(finalFolder, prodPayload.hub_id);
  return finalFolder;
}

/** Fetch + write a preview's linked production hub as a read-only mirror. Best-effort. */
async function mirrorLinkedProduction(
  client: ApiClient,
  workspaceDir: string,
  productionHubId: string,
  organizationId?: string,
): Promise<void> {
  try {
    const prodPayload = await client.pull(productionHubId, organizationId);
    const folder = await writeProductionMirror(workspaceDir, prodPayload);
    console.log(`Mirrored production hub → ${folder} (read-only)`);
  } catch (err) {
    console.warn(`Warning: could not mirror production hub (${err instanceof Error ? err.message : String(err)}). Preview pull is unaffected.`);
  }
}

/** Marker that an agent/human is editing a read-only mirror, not source. Cosmetic — never throws. */
const MIRROR_MARKER_PREFIX = '# Read-only mirror of production hub';
function prependMirrorMarker(hubFolder: string, productionHubId: string): void {
  const hubYaml = path.join(hubFolder, 'hub.yaml');
  try {
    const content = fs.readFileSync(hubYaml, 'utf-8');
    if (content.startsWith(MIRROR_MARKER_PREFIX)) return;
    const marker = `${MIRROR_MARKER_PREFIX} ${productionHubId}. Edits are ignored; push is blocked. Edit the linked preview hub instead.\n`;
    fs.writeFileSync(hubYaml, marker + content, 'utf-8');
  } catch {
    // Marker is cosmetic; the route/DO guards are the real protection.
  }
}

/**
 * Compare two connection arrays by id set.
 * Connections are read-only references — we only care whether the set changed
 * (added, removed, or renamed), not about field-level diffs.
 */
type ConnectionRef = { id?: string; name: string; type: string; service?: string };
export function connectionsEqual(
  a: ConnectionRef[] | undefined,
  b: ConnectionRef[] | undefined,
): boolean {
  const listA = a || [];
  const listB = b || [];
  if (listA.length !== listB.length) return false;

  // Build a comparable key per connection (id if available, otherwise name+type+service)
  const key = (c: ConnectionRef) =>
    c.id || `${c.name}|${c.type}|${c.service || ''}`;

  const setA = new Set(listA.map(key));
  const setB = new Set(listB.map(key));
  if (setA.size !== setB.size) return false;
  for (const k of setA) {
    if (!setB.has(k)) return false;
  }
  return true;
}
